import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOnboarding } from '../../contexts/OnboardingContext'
import { tokenStorage } from '../../utils/tokenStorage'
import { apiClient } from '../../services/apiClient'

/** Response shape from POST /api/v1/auth/register-onboarding */
interface RegisterOnboardingResponse {
  user: {
    id: string
    email: string
    name: string
    role: string
    businessId: string
  }
  tokens: {
    accessToken: string
    refreshToken: string
    expiresIn: number
  }
}

export interface UserDetailsStepProps {
  onBack: () => void
}

interface FieldErrors {
  name?: string
  email?: string
  password?: string
}

interface PasswordRequirement {
  label: string
  met: boolean
}

// --- Validation helpers ---

function validateName(name: string): string | undefined {
  const trimmed = name.trim()
  if (trimmed.length === 0) {
    return 'Full name is required'
  }
  if (trimmed.length < 2) {
    return 'Name must be at least 2 characters'
  }
  if (trimmed.length > 100) {
    return 'Name must not exceed 100 characters'
  }
  return undefined
}

function validateEmail(email: string): string | undefined {
  const trimmed = email.trim()
  if (trimmed.length === 0) {
    return 'Email address is required'
  }
  if (trimmed.length > 254) {
    return 'Email must not exceed 254 characters'
  }
  // Basic email format: local-part@domain, domain has at least one dot
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(trimmed)) {
    return 'Please enter a valid email address'
  }
  return undefined
}

function validatePassword(password: string): string | undefined {
  if (password.length === 0) {
    return 'Password is required'
  }
  if (password.length < 8) {
    return 'Password must be at least 8 characters'
  }
  if (password.length > 128) {
    return 'Password must not exceed 128 characters'
  }
  if (!/[A-Z]/.test(password)) {
    return 'Password must contain at least one uppercase letter'
  }
  if (!/[a-z]/.test(password)) {
    return 'Password must contain at least one lowercase letter'
  }
  if (!/[0-9]/.test(password)) {
    return 'Password must contain at least one digit'
  }
  return undefined
}

function getPasswordRequirements(password: string): PasswordRequirement[] {
  return [
    { label: '8–128 characters', met: password.length >= 8 && password.length <= 128 },
    { label: 'At least one uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'At least one lowercase letter', met: /[a-z]/.test(password) },
    { label: 'At least one digit', met: /[0-9]/.test(password) },
  ]
}

/**
 * UserDetailsStep collects user name, email, and password.
 * Validates on blur with inline errors and shows password requirements.
 * On submit, calls POST /api/v1/auth/register-onboarding with all wizard data.
 */
export function UserDetailsStep({ onBack }: UserDetailsStepProps) {
  const { state, dispatch } = useOnboarding()
  const navigate = useNavigate()

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { userName, userEmail, userPassword } = state

  // --- Field change handlers ---

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      dispatch({ type: 'SET_USER_DETAILS', payload: { userName: value } })
      // Clear error while typing
      if (fieldErrors.name) {
        setFieldErrors((prev) => ({ ...prev, name: undefined }))
      }
    },
    [dispatch, fieldErrors.name]
  )

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      dispatch({ type: 'SET_USER_DETAILS', payload: { userEmail: value } })
      if (fieldErrors.email) {
        setFieldErrors((prev) => ({ ...prev, email: undefined }))
      }
    },
    [dispatch, fieldErrors.email]
  )

  const handlePasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      dispatch({ type: 'SET_USER_DETAILS', payload: { userPassword: value } })
      if (fieldErrors.password) {
        setFieldErrors((prev) => ({ ...prev, password: undefined }))
      }
    },
    [dispatch, fieldErrors.password]
  )

  // --- Blur validation ---

  const handleNameBlur = () => {
    const error = validateName(userName)
    setFieldErrors((prev) => ({ ...prev, name: error }))
  }

  const handleEmailBlur = () => {
    const error = validateEmail(userEmail)
    setFieldErrors((prev) => ({ ...prev, email: error }))
  }

  const handlePasswordBlur = () => {
    const error = validatePassword(userPassword)
    setFieldErrors((prev) => ({ ...prev, password: error }))
  }

  // --- Form submission ---

  const isFormValid = (): boolean => {
    const nameError = validateName(userName)
    const emailError = validateEmail(userEmail)
    const passwordError = validatePassword(userPassword)
    return !nameError && !emailError && !passwordError
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate all fields
    const nameError = validateName(userName)
    const emailError = validateEmail(userEmail)
    const passwordError = validatePassword(userPassword)

    const errors: FieldErrors = {
      name: nameError,
      email: emailError,
      password: passwordError,
    }

    setFieldErrors(errors)

    if (nameError || emailError || passwordError) {
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const requestBody = {
        // Step 1
        businessType: state.businessType as
          | 'sole_proprietorship'
          | 'partnership'
          | 'private_limited'
          | 'public_limited'
          | 'llp',
        // Step 2 — Primary
        identifierType: state.identifierType as 'gstin' | 'pan' | 'udyam',
        identifierValue: state.identifierValue,
        verifiedBusinessName: state.verifiedBusinessName || '',
        verifiedAddress: state.verifiedAddress || '',
        crossIdentifiers: state.crossIdentifiers || undefined,
        // Step 2 — Secondary (optional)
        secondaryIdentifierType: state.secondaryIdentifierType || null,
        secondaryIdentifierValue: state.secondaryIdentifierValue || null,
        // Step 3
        industryCluster: state.selectedIndustryCluster || '',
        industry: state.selectedIndustry || '',
        // Step 4
        name: userName.trim(),
        email: userEmail.trim(),
        password: userPassword,
      }

      const response = await apiClient.post<RegisterOnboardingResponse>(
        '/auth/register-onboarding',
        requestBody
      )

      const { tokens } = response.data

      // Store tokens using the project's token storage utility
      tokenStorage.setTokensWithTimestamp({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        expiresIn: tokens.expiresIn,
        tokenType: 'Bearer',
      })

      // Redirect to dashboard
      navigate('/dashboard')
    } catch (error: any) {
      // Map server error codes to user-friendly messages
      const code = error?.code
      const message = error?.message

      if (code === 'USER_EXISTS' || message?.toLowerCase().includes('already exists')) {
        setSubmitError(
          'An account with this email already exists. Please sign in or use a different email.'
        )
        setFieldErrors((prev) => ({ ...prev, email: 'This email is already registered' }))
      } else if (code === 'INVALID_PASSWORD' || message?.toLowerCase().includes('password')) {
        setSubmitError(message || 'Password does not meet the requirements.')
        setFieldErrors((prev) => ({
          ...prev,
          password: message || 'Password does not meet requirements',
        }))
      } else if (code === 'INVALID_NAME' || message?.toLowerCase().includes('name')) {
        setSubmitError(message || 'Name does not meet the requirements.')
        setFieldErrors((prev) => ({ ...prev, name: message || 'Invalid name' }))
      } else if (code === 'INVALID_EMAIL' || message?.toLowerCase().includes('email')) {
        setSubmitError(message || 'Invalid email address.')
        setFieldErrors((prev) => ({ ...prev, email: message || 'Invalid email' }))
      } else if (code === 'TRANSACTION_FAILED') {
        setSubmitError('Registration could not be completed. Please try again.')
      } else if (message?.toLowerCase().includes('network') || message?.toLowerCase().includes('connection')) {
        setSubmitError('Connection problem. Please check your internet and try again.')
      } else {
        setSubmitError(message || 'Registration could not be completed. Please try again.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const passwordRequirements = getPasswordRequirements(userPassword)

  return (
    <div className="w-full max-w-2xl mx-auto">
      <h2 className="text-2xl font-semibold text-secondary-900 mb-2">Create your account</h2>
      <p className="text-secondary-500 mb-6">
        Enter your details to complete registration.
      </p>

      <form onSubmit={handleSubmit} noValidate>
        {/* Full Name */}
        <div className="mb-5">
          <label
            htmlFor="user-name"
            className="block text-sm font-medium text-secondary-700 mb-1"
          >
            Full Name
          </label>
          <input
            id="user-name"
            type="text"
            value={userName}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            placeholder="Enter your full name"
            maxLength={100}
            aria-invalid={!!fieldErrors.name}
            aria-describedby={fieldErrors.name ? 'name-error' : undefined}
            className={`
              w-full px-4 py-2.5 rounded-lg border text-sm text-gray-900 transition-colors
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1
              ${fieldErrors.name
                ? 'border-red-400 bg-red-50'
                : 'border-secondary-300 bg-white hover:border-secondary-400'
              }
            `}
          />
          {fieldErrors.name && (
            <p id="name-error" role="alert" className="mt-1 text-xs text-red-600">
              {fieldErrors.name}
            </p>
          )}
          <p className="mt-1 text-xs text-secondary-400">2–100 characters</p>
        </div>

        {/* Email */}
        <div className="mb-5">
          <label
            htmlFor="user-email"
            className="block text-sm font-medium text-secondary-700 mb-1"
          >
            Email Address
          </label>
          <input
            id="user-email"
            type="email"
            value={userEmail}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            placeholder="you@example.com"
            maxLength={254}
            aria-invalid={!!fieldErrors.email}
            aria-describedby={fieldErrors.email ? 'email-error' : undefined}
            className={`
              w-full px-4 py-2.5 rounded-lg border text-sm text-gray-900 transition-colors
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1
              ${fieldErrors.email
                ? 'border-red-400 bg-red-50'
                : 'border-secondary-300 bg-white hover:border-secondary-400'
              }
            `}
          />
          {fieldErrors.email && (
            <p id="email-error" role="alert" className="mt-1 text-xs text-red-600">
              {fieldErrors.email}
            </p>
          )}
        </div>

        {/* Password */}
        <div className="mb-5">
          <label
            htmlFor="user-password"
            className="block text-sm font-medium text-secondary-700 mb-1"
          >
            Password
          </label>
          <input
            id="user-password"
            type="password"
            value={userPassword}
            onChange={handlePasswordChange}
            onBlur={handlePasswordBlur}
            placeholder="Create a strong password"
            maxLength={128}
            aria-invalid={!!fieldErrors.password}
            aria-describedby="password-requirements password-error"
            className={`
              w-full px-4 py-2.5 rounded-lg border text-sm text-gray-900 transition-colors
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1
              ${fieldErrors.password
                ? 'border-red-400 bg-red-50'
                : 'border-secondary-300 bg-white hover:border-secondary-400'
              }
            `}
          />
          {fieldErrors.password && (
            <p id="password-error" role="alert" className="mt-1 text-xs text-red-600">
              {fieldErrors.password}
            </p>
          )}

          {/* Password requirements checklist */}
          <ul
            id="password-requirements"
            className="mt-2 space-y-1"
            aria-label="Password requirements"
          >
            {passwordRequirements.map((req) => (
              <li
                key={req.label}
                className={`flex items-center gap-2 text-xs ${
                  userPassword.length === 0
                    ? 'text-secondary-400'
                    : req.met
                      ? 'text-green-600'
                      : 'text-red-500'
                }`}
              >
                <span aria-hidden="true" className="flex-shrink-0">
                  {userPassword.length === 0 ? '○' : req.met ? '✓' : '✗'}
                </span>
                <span>{req.label}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Submit error */}
        {submitError && (
          <div
            role="alert"
            className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700"
          >
            {submitError}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center justify-between mt-6">
          <button
            type="button"
            onClick={onBack}
            disabled={isSubmitting}
            className="px-5 py-2.5 rounded-lg text-sm font-medium text-secondary-600 border border-secondary-300 hover:bg-secondary-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Back
          </button>

          <button
            type="submit"
            disabled={isSubmitting || !isFormValid()}
            className={`
              px-6 py-2.5 rounded-lg text-sm font-medium transition-colors
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
              ${isSubmitting || !isFormValid()
                ? 'bg-secondary-200 text-secondary-400 cursor-not-allowed'
                : 'bg-primary-600 text-white hover:bg-primary-700'
              }
            `}
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Creating account…
              </span>
            ) : (
              'Create Account'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
